<div
     snAssistantSurface="telecomAccount"
     i18n-surfaceTitle="@@telecomAccount.surface"
     surfaceTitle="Telecom account" class="telecom-account-container">

  <p-breadcrumb [model]="breadcrumbItems()" [home]="homeBreadcrumb" />

  @if (isLoading()) {
    <div class="loading-container">
      <p-progressSpinner />
    </div>
  } @else if (account(); as acct) {

    <!-- Header -->
    <div class="page-header">
      <div class="title-section">
        <h1>{{ acct.account_identifier || '—' }}</h1>
        <div class="header-meta">
          <p-tag [value]="providerLabel" [severity]="providerSeverity"></p-tag>
          @if (acct.id) {
            <span class="meta-id" pTooltip="ID" tooltipPosition="top">{{ acct.id }}</span>
          }
        </div>
      </div>
      <div class="header-actions">
        <p-button
          icon="pi pi-check-circle"
          severity="secondary"
          [outlined]="true"
          [loading]="isVerifying()"
          (click)="verify()"
          snAssistantAction="verify"
          actionHint="Asks the provider whether these credentials still work. Changes nothing, but tells you why calls fail."
          i18n-label="@@telecomAccount.btn.verify"
          label="Verify">
        </p-button>
        @if (isCustom()) {
          <p-button
            icon="pi pi-refresh"
            severity="secondary"
            [outlined]="true"
            (click)="askReprovision()"
            snAssistantAction="reprovision"
            actionDangerous
            actionHint="Rebuilds the SIP trunks of this account on our side. Calls in progress may drop while it runs."
            [disabled]="isReprovisioning()"
            i18n-label="@@telecomAccount.btn.reprovision"
            label="Reprovision SIP trunks">
          </p-button>
        }
        <p-button
          icon="pi pi-trash"
          severity="danger"
          [outlined]="true"
          (click)="askDelete()"
          snAssistantAction="delete"
          actionDangerous
          actionHint="Deletes the account. Every number connected through it stops receiving calls."
          [disabled]="isDeleting()"
          i18n-label="@@common.delete"
          label="Delete">
        </p-button>
      </div>
    </div>

    <!-- Verify result -->
    @if (verifyResult(); as v) {
      <p-card styleClass="verify-card">
        <div class="verify-header">
          <h3 i18n="@@telecomAccount.verify.title">Verification result</h3>
          @if (v.overall_ok) {
            <p-tag severity="success" i18n-value="@@telecomAccount.verify.overallOk" value="All checks passed"></p-tag>
          } @else {
            <p-tag severity="danger" i18n-value="@@telecomAccount.verify.overallFail" value="Some checks failed"></p-tag>
          }
        </div>
        <div class="verify-grid">
          <div class="verify-row">
            <span class="verify-label" i18n="@@telecomAccount.verify.localConfig">Local config</span>
            <p-tag [value]="checkLabel(v.local_config)" [severity]="checkSeverity(v.local_config)"></p-tag>
            @if (v.local_config?.reason) {
              <span class="verify-reason">{{ v.local_config?.reason }}</span>
            }
          </div>
          <div class="verify-row">
            <span class="verify-label" i18n="@@telecomAccount.verify.inboundTrunk">Inbound trunk</span>
            <p-tag [value]="checkLabel(v.inbound_trunk)" [severity]="checkSeverity(v.inbound_trunk)"></p-tag>
            @if (v.inbound_trunk?.reason) {
              <span class="verify-reason">{{ v.inbound_trunk?.reason }}</span>
            }
          </div>
          <div class="verify-row">
            <span class="verify-label" i18n="@@telecomAccount.verify.outboundTrunk">Outbound trunk</span>
            <p-tag [value]="checkLabel(v.outbound_trunk)" [severity]="checkSeverity(v.outbound_trunk)"></p-tag>
            @if (v.outbound_trunk?.reason) {
              <span class="verify-reason">{{ v.outbound_trunk?.reason }}</span>
            }
          </div>
          <div class="verify-row">
            <span class="verify-label" i18n="@@telecomAccount.verify.numbersInSync">Numbers in sync</span>
            <p-tag [value]="checkLabel(v.numbers_in_sync)" [severity]="checkSeverity(v.numbers_in_sync)"></p-tag>
            @if (v.numbers_in_sync?.reason) {
              <span class="verify-reason">{{ v.numbers_in_sync?.reason }}</span>
            }
          </div>
        </div>
      </p-card>
    }

    @if (isCustom() && sipGateway()) {
      <p-card styleClass="gateway-card">
        <div class="gateway-card-body">
          <div class="gateway-title">{{ appName }} SIP gateway</div>
          <app-sip-gateway-info [info]="sipGateway()" />
        </div>
      </p-card>

      <!-- Edit Custom SIP fields -->
      <p-card styleClass="edit-card">
        <div class="edit-header">
          <h3 i18n="@@telecomAccount.edit.title">SIP configuration</h3>
        </div>

        <form [formGroup]="editForm" (ngSubmit)="saveEdit()" class="edit-form">

          <div class="form-field">
            <label class="field-label">
              <span i18n="@@customSip.field.server">SIP server address</span>
            </label>
            <input
              [snAssistantId]="F.sipServerAddress"
              pInputText
              formControlName="sipServerAddress"
              placeholder="sip.mango.com:5060"
              class="w-full"
              i18n-pTooltip="@@customSip.tooltip.server"
              [pTooltip]="'host[:port] of YOUR SIP provider — not ' + appName + '. Leave empty to disable outbound.'"
              tooltipPosition="top" />
          </div>

          <div class="form-field">
            <label class="field-label">
              <span i18n="@@customSip.field.transport">SIP transport</span>
              <span class="required-marker">*</span>
            </label>
            <p-select
              [snAssistantId]="F.sipTransport"
              formControlName="sipTransport"
              [options]="transportOptions"
              styleClass="w-full">
            </p-select>
          </div>

          <div class="form-field">
            <label class="field-label">
              <span i18n="@@customSip.field.country">Destination country</span>
              <span class="required-marker">*</span>
            </label>
            <input
              [snAssistantId]="F.sipDestinationCountry"
              pInputText
              formControlName="sipDestinationCountry"
              placeholder="RU"
              maxlength="2"
              class="w-full short-input"
              i18n-pTooltip="@@customSip.tooltip.country"
              [pTooltip]="'ISO-3166 α-2 (2 letters). Hint for ' + appName + ' billing/routing — NOT a destination restriction.'"
              tooltipPosition="top"
              [class.p-invalid]="isInvalid('sipDestinationCountry')" />
            @if (isInvalid('sipDestinationCountry')) {
              <small class="field-error" i18n="@@customSip.err.country">
                Must be 2 uppercase letters (e.g. RU, US).
              </small>
            }
          </div>

          <div class="form-field">
            <label class="field-label">
              <span i18n="@@customSip.field.allowedAddresses">Allowed inbound IPs</span>
              <span class="required-marker">*</span>
            </label>
            <input
              [snAssistantId]="F.sipAllowedAddresses"
              pInputText
              formControlName="sipAllowedAddresses"
              placeholder="1.2.3.4, 10.0.0.0/24"
              class="w-full"
              i18n-pTooltip="@@customSip.tooltip.allowedAddresses"
              pTooltip="Comma- or space-separated list of IPv4 or IPv4/CIDR. IPs of YOUR SIP provider."
              tooltipPosition="top"
              [class.p-invalid]="allowedAddressesInvalid().length > 0" />
            @if (allowedAddressesInvalid().length > 0) {
              <small class="field-error">
                <span i18n="@@customSip.err.invalidCidr">Invalid IPv4 / CIDR:</span>
                {{ allowedAddressesInvalid().join(', ') }}
              </small>
            }
          </div>

          <div class="auth-section">
            <div class="auth-section-title">Outbound auth ({{ appName }} → provider, optional)</div>
            <div class="auth-section-hint" i18n="@@customSip.section.outboundHint">
              Set both for Digest auth, or leave both empty for IP-trust mode (Mango SIP-trunk, Plivo, etc.).
            </div>

            <div class="form-field">
              <label class="field-label" i18n="@@customSip.field.outboundAuthUsername">Outbound SIP username</label>
              <input
                [snAssistantId]="F.sipOutboundAuthUsername"
                pInputText
                formControlName="sipOutboundAuthUsername"
                class="w-full"
                i18n-pTooltip="@@customSip.tooltip.outboundAuthPair"
                pTooltip="Either set BOTH username and password (Digest auth), or leave BOTH empty (IP-trust mode)."
                tooltipPosition="top" />
            </div>

            <div class="form-field">
              <label class="field-label">
                <span i18n="@@customSip.field.outboundAuthPassword">Outbound SIP password</span>
                @if (hasOutboundAuthPassword()) {
                  <p-tag severity="success" i18n-value="@@telecomAccount.field.passwordSet" value="set"></p-tag>
                } @else {
                  <p-tag severity="secondary" i18n-value="@@telecomAccount.field.ipTrust" value="IP-trust"></p-tag>
                }
              </label>
              <input
                [snAssistantId]="F.sipOutboundAuthPassword"
                pInputText
                type="password"
                formControlName="sipOutboundAuthPassword"
                i18n-placeholder="@@telecomAccount.field.inboundPasswordPlaceholder"
                placeholder="(empty = keep / clear to disable inbound auth)"
                class="w-full"
                i18n-pTooltip="@@telecomAccount.tooltip.pwdSemantics"
                pTooltip="Empty + untouched = keep current. Cleared explicitly = disable auth (clear username too). Non-empty = replace."
                tooltipPosition="top" />
            </div>

            @if (hasOutboundAuthPairError()) {
              <p-message
                severity="warn"
                [closable]="false"
                i18n-text="@@customSip.err.outboundAuthPair"
                text="Outbound username and password must be set together, or both left empty.">
              </p-message>
            }
          </div>

          <div class="auth-section">
            <div class="auth-section-title">Inbound auth (provider → {{ appName }}, optional)</div>
            <div class="auth-section-hint" i18n="@@customSip.section.inboundHint">
              Most providers don't challenge inbound — IP allowlist above is the gate. Leave both empty unless your provider requires Digest auth.
            </div>

            <div class="form-field">
              <label class="field-label" i18n="@@customSip.field.inboundAuthUsername">Inbound SIP username</label>
              <input
                [snAssistantId]="F.sipInboundAuthUsername"
                pInputText
                formControlName="sipInboundAuthUsername"
                class="w-full"
                i18n-pTooltip="@@customSip.tooltip.inboundAuthPair"
                pTooltip="Either set BOTH username and password, or leave BOTH empty (IP-only inbound auth)."
                tooltipPosition="top" />
            </div>

            <div class="form-field">
              <label class="field-label">
                <span i18n="@@customSip.field.inboundAuthPassword">Inbound SIP password</span>
                @if (hasInboundAuthPassword()) {
                  <p-tag severity="success" i18n-value="@@telecomAccount.field.passwordSet" value="set"></p-tag>
                } @else {
                  <p-tag severity="secondary" i18n-value="@@telecomAccount.field.ipTrust" value="IP-trust"></p-tag>
                }
              </label>
              <input
                [snAssistantId]="F.sipInboundAuthPassword"
                pInputText
                type="password"
                formControlName="sipInboundAuthPassword"
                i18n-placeholder="@@telecomAccount.field.inboundPasswordPlaceholder"
                placeholder="(empty = keep / clear to disable inbound auth)"
                class="w-full"
                i18n-pTooltip="@@telecomAccount.tooltip.pwdSemantics"
                pTooltip="Empty + untouched = keep current. Cleared explicitly = disable auth (clear username too). Non-empty = replace."
                tooltipPosition="top" />
            </div>

            @if (hasInboundAuthPairError()) {
              <p-message
                severity="warn"
                [closable]="false"
                i18n-text="@@customSip.err.inboundAuthPair"
                text="Inbound username and password must be set together, or both left empty.">
              </p-message>
            }
          </div>

          <div class="form-actions">
            <p-button
              type="submit"
              [icon]="isSaving() ? 'pi pi-spin pi-spinner' : 'pi pi-check'"
              [disabled]="editForm.invalid || isSaving()"
              snAssistantAction="save"
              actionHint="Writes the SIP settings of this account. Wrong values here silence every number behind it."
              i18n-label="@@common.save"
              label="Save">
            </p-button>
          </div>
        </form>
      </p-card>
    }

  }

</div>

<!-- Reprovision confirmation -->
<app-delete-confirmation
  [visible]="reprovisionConfirmVisible()"
  (visibleChange)="reprovisionConfirmVisible.set($event)"
  [title]="reprovisionConfirmTitle"
  [subtitle]="reprovisionConfirmSubtitle"
  [deleting]="isReprovisioning()"
  (confirm)="confirmReprovision()"
  (cancel)="cancelReprovision()" />

<!-- Delete confirmation -->
<app-delete-confirmation
  [visible]="deleteConfirmVisible()"
  (visibleChange)="deleteConfirmVisible.set($event)"
  [title]="deleteConfirmTitle"
  [subtitle]="deleteConfirmSubtitle"
  [deleting]="isDeleting()"
  (confirm)="confirmDelete()"
  (cancel)="cancelDelete()" />
